/**
 * ui.tsx — Componentes UI reutilizáveis
 * Extraídos de Layout.tsx para melhor organização
 */
import React, { useState, useEffect, Component, ReactNode, ErrorInfo } from 'react';
import { Link } from 'react-router-dom';
import {
  LucideIcon,
  Home,
  ArrowRight,
  Info,
} from 'lucide-react';

// ─── Section Header ──────────────────────────────────────────────────
export const SectionHeader: React.FC<{
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  className?: string;
}> = ({ title, subtitle, icon: Icon, className = "" }) => (
  <div className={`mb-12 md:mb-16 ${className}`}>
    <div className="flex items-center gap-5 mb-4">
      {Icon && (
        <div className="p-4 bg-[#14B8A6]/10 rounded-2xl text-[#14B8A6] hidden sm:flex shadow-inner">
          <Icon size={32} />
        </div>
      )}
      <div>
        <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight leading-none">
          {title}
        </h2>
        {subtitle && (
          <p className="text-slate-400 mt-3 text-sm md:text-base max-w-2xl font-medium">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </div>
);

// ─── Card ────────────────────────────────────────────────────────────
export const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}> = ({ children, className = "", onClick }) => (
  <div
    onClick={onClick}
    className={`
      bg-[#0F172A]/40 backdrop-blur-lg border border-white/[0.08] rounded-2xl overflow-hidden 
      hover:border-[#14B8A6]/30 hover:shadow-2xl hover:shadow-black/60 hover:-translate-y-1 transition-all duration-500 group relative gpu-layer
      ${onClick ? "cursor-pointer active:scale-[0.98]" : ""} 
      ${className}
    `}
  >
    <div className="absolute inset-0 bg-gradient-to-tr from-[#14B8A6]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none"></div>
    <div className="relative z-10">{children}</div>
  </div>
);

// ─── Shimmer (loading placeholder) ──────────────────────────────────
export const Shimmer: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <div className={`relative overflow-hidden bg-white/5 rounded ${className}`}>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
  </div>
);

// ─── Badge ───────────────────────────────────────────────────────────
export const Badge: React.FC<{
  children: React.ReactNode;
  color?: "emerald" | "blue" | "amber" | "red" | "purple" | "slate";
}> = ({ children, color = "emerald" }) => {
  const colors = {
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    slate: "bg-slate-500/10 text-slate-400 border-slate-500/20",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${colors[color]}`}
    >
      {children}
    </span>
  );
};

// ─── Alert ───────────────────────────────────────────────────────────
export const Alert: React.FC<{
  children: React.ReactNode;
  type?: "info" | "warning" | "error" | "success";
}> = ({ children, type = "info" }) => {
  const styles = {
    info: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    warning: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    error: "bg-red-500/10 text-red-400 border-red-500/20",
    success: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };
  return (
    <div
      className={`p-4 rounded-xl border flex gap-3 items-start animate-fade-in ${styles[type]}`}
    >
      <Info size={18} className="shrink-0 mt-0.5" />
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
};

// ─── Breadcrumbs ─────────────────────────────────────────────────────
export const Breadcrumbs: React.FC<{
  items: { label: string; path?: string }[];
}> = ({ items }) => (
  <nav 
    className="flex items-center gap-2 text-xs text-slate-500 mb-6 font-mono overflow-x-auto whitespace-nowrap pb-2 md:pb-0 scrollbar-hide"
    aria-label="Navegação por trilha"
  >
    <Link
      to="/"
      className="hover:text-[#14B8A6] transition-colors flex items-center gap-1"
    >
      <Home size={12} /> Início
    </Link>
    {items.map((item, i) => (
      <React.Fragment key={i}>
        <span className="text-slate-700" aria-hidden="true">/</span>
        {item.path ? (
          <Link
            to={item.path}
            className="hover:text-[#14B8A6] transition-colors"
          >
            {item.label}
          </Link>
        ) : (
          <span className="text-[#14B8A6] font-bold" aria-current="page">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

// ─── Scroll to Top ───────────────────────────────────────────────────
export const ScrollToTop: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 500);
    window.addEventListener("scroll", toggleVisibility, { passive: true });
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-8 right-8 p-4 bg-[#14B8A6] text-slate-900 rounded-full shadow-2xl z-[60] transition-all duration-300 hover:scale-110 hover:-translate-y-1 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}`}
      aria-label="Voltar ao topo da página"
    >
      <ArrowRight size={24} className="-rotate-90" />
    </button>
  );
};

// ─── Info Tooltip ────────────────────────────────────────────────────
export const InfoTooltip: React.FC<{ content: string; title?: string }> = ({
  content,
  title,
}) => (
  <div className="relative group/tooltip inline-block ml-1 align-middle">
    <Info
      size={12}
      className="text-slate-500 hover:text-hytale-accent cursor-help transition-colors"
      aria-label={title || 'Mais informações'}
    />
    <div
      className="
      absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3
      bg-slate-950 border border-white/10 rounded-lg shadow-2xl backdrop-blur-xl
      opacity-0 scale-95 group-hover/tooltip:opacity-100 group-hover/tooltip:scale-100 
      transition-all duration-200 pointer-events-none z-[70] origin-bottom
    "
      role="tooltip"
    >
      {title && (
        <div className="text-[10px] font-bold text-hytale-gold uppercase tracking-widest mb-1">
          {title}
        </div>
      )}
      <div className="text-xs text-slate-300 leading-relaxed font-sans normal-case whitespace-normal">
        {content}
      </div>
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-slate-950"></div>
    </div>
  </div>
);

// ─── Error Boundary ──────────────────────────────────────────────────
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  constructor(props: ErrorBoundaryProps) {
    super(props);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Erro capturado:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="text-red-400" size={32} />
            </div>
            <h3 className="text-red-400 font-bold text-lg mb-2">Algo deu errado</h3>
            <p className="text-slate-500 text-sm mb-4">
              {this.state.error?.message || 'Ocorreu um erro inesperado.'}
            </p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-6 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl transition-all border border-red-500/20 font-bold text-sm"
              aria-label="Tentar novamente e recarregar a página"
            >
              Recarregar Página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─── Loading Skeletons ───────────────────────────────────────────────
export const CardSkeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`bg-[#0F172A]/40 border border-white/[0.06] rounded-2xl overflow-hidden animate-pulse ${className}`}>
    <div className="h-48 bg-white/[0.03]"></div>
    <div className="p-5 space-y-3">
      <div className="h-4 bg-white/[0.06] rounded w-3/4"></div>
      <div className="h-3 bg-white/[0.04] rounded w-full"></div>
      <div className="h-3 bg-white/[0.04] rounded w-2/3"></div>
    </div>
  </div>
);

export const ListSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="bg-[#0F172A]/40 border border-white/[0.06] rounded-xl p-4 flex items-center gap-4 animate-pulse">
        <div className="w-10 h-10 rounded-full bg-white/[0.06] shrink-0"></div>
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-white/[0.06] rounded w-1/3"></div>
          <div className="h-2.5 bg-white/[0.04] rounded w-2/3"></div>
        </div>
      </div>
    ))}
  </div>
);

export const PageSkeleton: React.FC = () => (
  <div className="space-y-8 animate-pulse">
    <div className="space-y-3">
      <div className="h-8 bg-white/[0.06] rounded w-1/3"></div>
      <div className="h-4 bg-white/[0.04] rounded w-2/3"></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
    </div>
  </div>
);
